import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import Card from "../ui/Card";
import StarRating from "../ui/StarRating";
import DirectionArrow from "../ui/DirectionArrow";
import { Photo } from "../../lib/api";
import { osgbToWGS84 } from "../../lib/coordinates";
import { Link2 } from "lucide-react";
import { useConditionInfo } from "../../hooks/useConditionInfo";
import { formatTypeName } from "../../lib/trigTypeName";
import FacebookShareButton from "../ui/FacebookShareButton";
import { getCanonicalOrigin } from "../../lib/canonicalOrigin";

interface Log {
  id: number;
  trig_id: number;
  user_id: number;
  trig_name?: string;
  user_name?: string;
  trig_lat?: number | null;
  trig_lon?: number | null;
  trig_condition?: string | null;
  trig_status_name?: string | null;
  trig_type_code?: string | null;
  trig_type_name?: string | null;
  trig_category_code?: string | null;
  trig_category_name?: string | null;
  trig_variant_name?: string | null;
  date: string;
  time: string;
  condition: string;
  comment: string;
  score: number;
  osgb_gridref?: string;
  osgb_eastings?: number;
  osgb_northings?: number;
  location_distance_m?: number;
  distance_km?: number | null;
  user_has_avatar?: boolean;
  photos?: Photo[];
}

interface LogCardProps {
  log: Log;
  // Deprecated: use log.trig_name and log.user_name instead
  userName?: string;
  trigName?: string;
  onPhotoUpdate?: () => void;
  isCurrentUserLog?: boolean;
  /** Show distance from filter center point (uses log.distance_km) */
  showDistance?: boolean;
  /** Show the curated trig condition icon before the TP number */
  showTrigCondition?: boolean;
  /** Show the trig header line (waypoint, name, type). Default: true */
  showTrigInfo?: boolean;
  /** Show admin OG preview link */
  isAdmin?: boolean;
  /** Always show the Facebook share button (for detail pages) */
  showShareButton?: boolean;
  /** Optional action elements rendered in the header (e.g. add-to-list button) */
  actions?: ReactNode;
}


// Helper to get status icon info
function getStatusInfo(statusName?: string | null): { icon?: string; abbrev: string } {
  const statusMap: Record<string, { icon: string; abbrev: string }> = {
    Pillar: { icon: "/icons/t_pillar.png", abbrev: "P" },
    "Major mark": { icon: "/icons/t_fbm.png", abbrev: "MM" },
    "Minor mark": { icon: "/icons/t_passive.png", abbrev: "m" },
    Intersected: { icon: "/icons/t_intersected.png", abbrev: "I" },
    "User Added": { icon: "/icons/t_user_added.svg", abbrev: "UA" },
    Controversial: { icon: "/icons/t_controversial.svg", abbrev: "C" },
  };
  
  const normalizedStatusName = statusName?.trim();
  
  if (normalizedStatusName && statusMap[normalizedStatusName]) {
    return statusMap[normalizedStatusName];
  }
  
  // Fallback check for case-insensitive match
  if (normalizedStatusName) {
    const lowerName = normalizedStatusName.toLowerCase();
    const match = Object.keys(statusMap).find(key => key.toLowerCase() === lowerName);
    if (match) {
      return statusMap[match];
    }
  }
  
  return { abbrev: "?" };
}

export default function LogCard({ log, userName, trigName, isCurrentUserLog = false, showDistance = false, showTrigCondition = false, showTrigInfo = true, isAdmin = false, showShareButton = false, actions }: LogCardProps) {
  const navigate = useNavigate();
  const { getConditionInfo } = useConditionInfo();
  const conditionInfo = getConditionInfo(log.condition);
  const formattedDate = new Date(log.date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  // Use denormalized fields if available, otherwise fall back to props
  const displayTrigName = log.trig_name || trigName;
  const displayUserName = log.user_name || userName;

  // Format trig ID with minimum 4 digits (TP0023, TP1234, TP34567)
  const formattedTrigId = `TP${log.trig_id.toString().padStart(4, '0')}`;

  // Calculate bearing from point A to point B (in degrees, 0 = North)
  const calculateBearing = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const toRad = (deg: number) => deg * (Math.PI / 180);
    const toDeg = (rad: number) => rad * (180 / Math.PI);
    
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δλ = toRad(lon2 - lon1);
    
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    const θ = Math.atan2(y, x);
    
    return (toDeg(θ) + 360) % 360; // Normalize to 0-360
  };

  // Format distance with direction arrow
  const formatDistance = (distance?: number) => {
    if (distance === undefined || distance === null) return null;
    
    const distanceText = distance < 1000 
      ? `${Math.round(distance)}m` 
      : `${(distance / 1000).toFixed(1)}km`;
    
    const colorClass = distance <= 25 ? 'text-gray-500' : 'text-red-700';
    
    // Calculate bearing if we have coordinates
    let bearing: number | null = null;
    if (log.trig_lat != null && log.trig_lon != null && 
        log.osgb_eastings !== undefined && log.osgb_northings !== undefined) {
      // Convert log OSGB to WGS84
      const logWGS84 = osgbToWGS84(log.osgb_eastings, log.osgb_northings);
      bearing = calculateBearing(log.trig_lat, log.trig_lon, logWGS84.lat, logWGS84.lon);
    }
    
    return (
      <span className={`${colorClass} inline-flex items-center gap-1`}>
        {distanceText}
        {bearing !== null && <DirectionArrow bearing={bearing} size={14} />}
      </span>
    );
  };

  const handlePhotoClick = (photo: Photo, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking a photo
    // Navigate with the photo data and all photos from the log in state
    // This enables forward/back navigation in the photo viewer
    navigate(`/photos/${photo.id}`, { 
      state: { 
        photo,
        allPhotos: log.photos,
        context: 'log' // Flag to indicate this came from a log
      } 
    });
  };

  const handleCardClick = () => {
    navigate(`/logs/${log.id}`);
  };

  // Apply light green background for current user's logs
  // Use !bg-green-50 to override Card's default bg-white
  const cardClassName = isCurrentUserLog
    ? "hover:shadow-lg transition-shadow cursor-pointer !bg-green-50 dark:!bg-green-900/30"
    : "hover:shadow-lg transition-shadow cursor-pointer";

  return (
    <Card 
      className={cardClassName}
      onClick={handleCardClick}
    >
      <div className="flex flex-col gap-3">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {showTrigInfo && (
              <Link
                to={`/trigs/${log.trig_id}`}
                className="inline-flex items-center gap-1.5 text-lg font-semibold text-trig-green-600 hover:text-trig-green-700 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Status icon (type) - always shown */}
                {log.trig_status_name && getStatusInfo(log.trig_status_name).icon && (
                  <img
                    src={getStatusInfo(log.trig_status_name).icon}
                    alt={getStatusInfo(log.trig_status_name).abbrev}
                    title={log.trig_status_name}
                    className="w-6 h-6 object-contain"
                  />
                )}
                {/* Trig condition icon (square) - only shown when enabled */}
                {showTrigCondition && log.trig_condition && (
                  <img
                    src={`/icons/conditions/${getConditionInfo(log.trig_condition).icon}`}
                    alt={getConditionInfo(log.trig_condition).label}
                    title={`Trig condition: ${getConditionInfo(log.trig_condition).label}`}
                    className="w-5 h-5"
                  />
                )}
                {formattedTrigId}
                {displayTrigName && (
                  <>
                    <span className="text-gray-400 dark:text-gray-500 mx-2">·</span>
                    <span className="font-normal text-gray-700 dark:text-gray-300">{displayTrigName}</span>
                  </>
                )}
                {log.trig_type_name && (
                  <>
                    <span className="text-gray-400 dark:text-gray-500 mx-1">·</span>
                    <span className="font-normal text-gray-500 dark:text-gray-400 text-sm">
                      {log.trig_type_code !== log.trig_category_code &&
                        `${log.trig_category_name} · `}
                      {formatTypeName(log.trig_type_name, log.trig_variant_name)}
                    </span>
                  </>
                )}
              </Link>
            )}
            {actions && showTrigInfo && (
              <span className="inline-flex items-center ml-2" onClick={(e) => e.stopPropagation()}>
                {actions}
              </span>
            )}
            <div className="flex flex-wrap items-center gap-2 text-base text-gray-600 dark:text-gray-400">
              <span className="inline-flex items-center gap-1.5">
                {log.user_has_avatar && (
                  <span className="w-6 h-6 flex-shrink-0 rounded-full bg-gray-200 dark:bg-gray-600 inline-block">
                    <img
                      src={`https://trigpointinguk-avatars.s3.amazonaws.com/U${log.user_id.toString().padStart(5, "0")}.jpg`}
                      alt=""
                      width={24}
                      height={24}
                      className="w-6 h-6 rounded-full object-cover opacity-0 transition-opacity duration-200"
                      onLoad={(e) => e.currentTarget.classList.replace("opacity-0", "opacity-100")}
                      onError={(e) => e.currentTarget.classList.add("hidden")}
                    />
                  </span>
                )}
                {displayUserName ? (
                  <Link
                    to={`/profile/${log.user_id}`}
                    className="text-trig-green-600 hover:underline font-semibold text-lg"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {displayUserName}
                  </Link>
                ) : (
                  <Link
                    to={`/profile/${log.user_id}`}
                    className="text-trig-green-600 hover:underline font-semibold text-lg"
                    onClick={(e) => e.stopPropagation()}
                  >
                    User #{log.user_id}
                  </Link>
                )}
              </span>
              <span className="text-gray-400 dark:text-gray-500">·</span>
              {/* Condition Icon */}
              <img 
                src={`/icons/conditions/${conditionInfo.icon}`}
                alt={conditionInfo.label}
                title={conditionInfo.label}
                className="w-4 h-4"
              />
              <StarRating 
                rating={log.score / 2} 
                size="sm" 
                title={`${log.score}/10`}
              />
              <span className="text-gray-400 dark:text-gray-500">·</span>
              <span className="text-gray-700 dark:text-gray-300">{formattedDate}</span>
              {log.time && log.time !== "12:00:00" && (
                <span className="text-gray-500 dark:text-gray-400">{log.time}</span>
              )}

              {/* Distance from filter center point */}
              {showDistance && log.distance_km != null && (
                <>
                  <span className="text-gray-400 dark:text-gray-500">·</span>
                  <span className="text-sm text-blue-600 font-medium">
                    {log.distance_km < 1 
                      ? `${Math.round(log.distance_km * 1000)}m away`
                      : `${log.distance_km.toFixed(1)}km away`}
                  </span>
                </>
              )}
              
              {/* Location and Distance from log point */}
              {log.osgb_gridref && log.location_distance_m !== undefined && (
                <>
                  <span className="text-gray-400 dark:text-gray-500">·</span>
                  <span className="text-gray-600 dark:text-gray-400 font-mono">{log.osgb_gridref}</span>
                  <span className="text-gray-400 dark:text-gray-500">·</span>
                  <span className="text-sm">{formatDistance(log.location_distance_m)}</span>
                </>
              )}

              {(isCurrentUserLog || showShareButton) && (
                <FacebookShareButton url={`${getCanonicalOrigin()}/logs/${log.id}`} />
              )}

              {isAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const apiBase = import.meta.env.VITE_API_BASE as string;
                    window.open(`${apiBase}/v1/logs/${log.id}/opengraph-image?refresh=1`, "_blank", "noopener,noreferrer");
                  }}
                  title="Preview OG image"
                  className="inline-flex items-center text-gray-400 hover:text-trig-green-600 dark:text-gray-500 dark:hover:text-trig-green-400 transition-colors"
                >
                  <Link2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Comment and Photos - photos float top-right, comment wraps beside
            them and then flows full-width beneath the image strip */}
        {(log.comment || (log.photos && log.photos.length > 0)) && (
          <div className="flow-root">
            {/* Photos - floated to the top-right. Because the strip's height is
                just the thumbnail row, its horizontal scrollbar sits directly
                under the photos rather than at the bottom of a stretched column. */}
            {log.photos && log.photos.length > 0 && (
              <div className="float-right ml-4 mb-2 max-w-[50%] flex gap-2 overflow-x-auto pb-2">
                {log.photos.slice(0, 20).map((photo) => (
                  <div
                    key={photo.id}
                    className="relative h-20 w-20 flex-shrink-0 cursor-pointer group"
                    onClick={(e) => handlePhotoClick(photo, e)}
                  >
                    <img
                      src={photo.icon_url}
                      alt={photo.caption}
                      className="h-full w-full object-cover rounded border border-gray-200 dark:border-gray-600 transition-all duration-200 group-hover:scale-110 group-hover:shadow-lg"
                      title={photo.caption}
                      width={80}
                      height={80}
                      loading="lazy"
                    />
                  </div>
                ))}
                {log.photos.length > 20 && (
                  <div className="h-20 w-20 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 flex-shrink-0 text-sm text-gray-600 dark:text-gray-400">
                    +{log.photos.length - 20}
                  </div>
                )}
              </div>
            )}

            {/* Comment - wraps to the left of the photos, then full width below */}
            {log.comment && (
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{log.comment}</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
